import React, { useCallback, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LeaderboardTable from './LeaderboardTable';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {useData} from '../../../contexts/DataContext';
import {useAuth} from '../../../contexts/AuthContext';
import { normalizePicks } from '../../../lib/normalizePicks';
import { getLogoUrl } from '../../../utils/getLogoUrl';
import { getDisplayName } from '../../../utils/getDisplayName';
import { isCurrentUser, formatPoints } from '../../../utils/leaderboardUtils';
import WeekSummary from './WeekSummary';
import YouChip from './YouChip';
import { ResultDot, WinPill } from './SoftMarks';
import { soft, softContainerSx, softHeadCellSx, softBodyCellSx, meRowFirstCellShadow, fadeWhileLoading } from './softStyles';

const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '5vh',
      marginRight: '5vh',
      minHeight: '70vh',
      textAlign: 'center',
      marginTop: '128px'
    },
    leaderboard: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      margin: 'auto',
      maxWidth: '80vw'
    },
    picks: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      margin: 'auto',
      width: '100%',
      boxSizing: 'border-box',
    },
    gameHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
    },
    icon: {
      width: '28px',
      height: '28px',
      objectFit: 'contain'
    },
    vs: {
      fontSize: '11px',
      color: soft.faint,
    },
    finalScore: {
      fontSize: '11px',
      color: soft.faint,
      fontWeight: 400,
      marginTop: '3px',
    },
    score: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '4px',
    },
  }

// Desktop picks table. The player column (left) and Week pts column (right)
// stay pinned while the game columns scroll sideways, so a busy week never
// wraps games onto a second line without names. Sticky cells need an opaque
// background or scrolled cells show through them.
const pickHeadCellSx = {
  ...softHeadCellSx,
  textAlign: 'center',
  padding: '10px 12px',
};
const pickCellSx = {
  ...softBodyCellSx,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  padding: '10px 12px',
};
const stickyNameSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  textAlign: 'left',
  minWidth: '140px',
  maxWidth: '180px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Soft edge instead of a hard divider while games scroll underneath.
  boxShadow: `1px 0 0 ${soft.outline}`,
};
const stickyWeekSx = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  textAlign: 'right',
  boxShadow: `-1px 0 0 ${soft.outline}`,
};

// Mobile card-per-game styles
const mobileStyles = {
  gameCard: {
    ...softContainerSx,
    marginBottom: '12px',
  },
  gameHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: soft.headBg,
  },
  gameLogo: {
    width: '36px',
    height: '36px',
    objectFit: 'contain',
  },
  vsText: {
    fontWeight: 500,
    fontSize: '13px',
    color: soft.faint,
  },
  finalScore: {
    textAlign: 'center',
    fontSize: '12px',
    color: soft.muted,
    padding: '0 0 8px',
    backgroundColor: soft.headBg,
  },
  pickRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    borderTop: `1px solid ${soft.hairline}`,
    fontSize: '14px',
    color: soft.text,
    backgroundColor: '#FFFFFF',
  },
  pickRowMe: {
    backgroundColor: soft.meBg,
    boxShadow: meRowFirstCellShadow,
  },
  pickName: {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '55%',
  },
  pickValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textAlign: 'right',
  },
  lockedText: {
    color: soft.faint,
    textAlign: 'right',
  },
};

const Leaderboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { getGamesForWeek,
        getAllPicksForWeek,
        getWeeklyPointsForWeek,
        getCurrentWeek,
        winningTeams,
        userDisplayNames,
        currentSeason,
        } = useData();
    const { isAdmin, currentUser } = useAuth();
    const [page, setPage] = useState(1);
    const [numberOfWeeks, setNumberOfWeeks] = React.useState(1);
    const [games, setGames] = useState([]);
    const [allPicksForWeek, setAllPicksForWeek] = useState([]);
    const [weeklyPoints, setWeeklyPoints] = useState({}); // email -> published score for the selected week
    const [isLoading, setIsLoading] = useState(false);
    const [expandedPick, setExpandedPick] = useState(null); // tracks which mobile pick-score row is expanded
    const [fetchError, setFetchError] = useState('');
    const season = currentSeason;

    // Picks are keyed by game doc id; normalize once per user (handles legacy
    // array-shaped picks too) so each cell can look up by game.id.
    const picksByUser = React.useMemo(() => {
        const map = {};
        (allPicksForWeek || []).forEach((user) => {
            map[user.name] = normalizePicks(user.picks, games);
        });
        return map;
    }, [allPicksForWeek, games]);

    // Games, everyone's picks, and the published weekly scores for one week.
    // Weekly scores are keyed by the same emails the picks come back with.
    const loadWeek = useCallback(async (weekNum) => {
        const [games, picks] = await Promise.all([
            getGamesForWeek(season, weekNum),
            getAllPicksForWeek(season, weekNum)
        ]);
        const points = await getWeeklyPointsForWeek(season, weekNum, picks.map((p) => p.name));
        return { games, picks, points };
    }, [season, getGamesForWeek, getAllPicksForWeek, getWeeklyPointsForWeek]);

    // Startup load and week changes share one request counter, so whichever
    // was asked for last wins (a week picked from the standings' breakdown
    // before the startup load finishes must not be overwritten by it).
    const turnPageRef = React.useRef(0);
    useEffect(() => {
        if (!season) return; // Wait for real season to load
        const requestId = ++turnPageRef.current;
        const isCurrent = () => requestId === turnPageRef.current;
        const fetchData = async () => {
            setIsLoading(true);
            setFetchError('');
            try {
                const currentWeek = await getCurrentWeek(season);
                if (!isCurrent()) {
                    setNumberOfWeeks(currentWeek);
                    return;
                }
                setPage(currentWeek);
                setNumberOfWeeks(currentWeek);

                const { games, picks, points } = await loadWeek(currentWeek);
                if (!isCurrent()) return;

                setGames(games);
                setAllPicksForWeek(picks);
                setWeeklyPoints(points);
            } catch (error) {
                console.error('Error loading leaderboard:', error);
                if (isCurrent()) setFetchError('Failed to load data. Please try refreshing.');
            } finally {
                if (isCurrent()) setIsLoading(false);
            }
        };

        fetchData();
        // Unmount or season change: invalidate this request.
        return () => { if (isCurrent()) turnPageRef.current += 1; };
    }, [season, getCurrentWeek, loadWeek]);

    const selectWeek = useCallback(async (weekNum) => {
        const requestId = ++turnPageRef.current;
        setPage(weekNum);
        setIsLoading(true);

        try {
            const { games, picks, points } = await loadWeek(weekNum);
            if (requestId !== turnPageRef.current) return;
            setGames(games);
            setAllPicksForWeek(picks);
            setWeeklyPoints(points);
        } catch (error) {
            console.error('Error loading week:', error);
            if (requestId === turnPageRef.current) {
                setFetchError('Failed to load week. Please try again.');
            }
        } finally {
            if (requestId === turnPageRef.current) setIsLoading(false);
        }
    }, [loadWeek]);

    // From the standings' weekly breakdown: open that week's picks below.
    const picksSectionRef = React.useRef(null);
    const showWeekPicks = useCallback((weekNum) => {
        selectWeek(weekNum);
        picksSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [selectWeek]);

    const turnPage = (event, value) => {
        selectWeek(typeof value === 'object' ? value.target.value : value);
    };

    const myPicks = (allPicksForWeek || []).find((user) => isCurrentUser(user.name, currentUser));

    // Mobile: card-per-game picks
    const renderMobilePicks = () => (
      <div style={{ width: '100%', padding: '0 4px', boxSizing: 'border-box', ...fadeWhileLoading(isLoading) }}>
        {games && games.map((game) => {
          const gameId = `${game.awayTeam?.name || 'away'}-${game.homeTeam?.name || 'home'}`;
          return (
            <div style={mobileStyles.gameCard} key={gameId}>
              <div style={mobileStyles.gameHeader}>
                {game.awayTeam.logo && (
                  <img style={mobileStyles.gameLogo} src={getLogoUrl(game.awayTeam.logo)} alt={game.awayTeam.name} />
                )}
                <span style={mobileStyles.vsText}>{game.neutralSite ? 'VS' : '@'}</span>
                {game.homeTeam.logo && (
                  <img style={mobileStyles.gameLogo} src={getLogoUrl(game.homeTeam.logo)} alt={game.homeTeam.name} />
                )}
              </div>
              {game.disabled && game.homeScore !== undefined && game.awayScore !== undefined && (
                <div style={mobileStyles.finalScore}>
                  Final {game.awayScore} – {game.homeScore}
                </div>
              )}
              {allPicksForWeek && allPicksForWeek.map((user) => {
                const pick = picksByUser[user.name]?.[game.id];
                const isMe = isCurrentUser(user.name, currentUser);
                const baseRowStyle = isMe ? { ...mobileStyles.pickRow, ...mobileStyles.pickRowMe } : mobileStyles.pickRow;
                if (!pick) {
                  return (
                    <div style={baseRowStyle} key={user.name}>
                      <span style={mobileStyles.pickName}>{getDisplayName(userDisplayNames.get(user.name), user.name)}{isMe && <YouChip />}</span>
                      <span style={mobileStyles.lockedText}>—</span>
                    </div>
                  );
                }

                // Derive winner from game scores — works for all past games without needing to run scoring
                const winningTeam = (game.homeScore !== undefined && game.awayScore !== undefined)
                  ? (Number(game.homeScore) > Number(game.awayScore) ? game.homeTeam.name : game.awayTeam.name)
                  : (winningTeams && winningTeams[gameId]);
                const pickedWinner = pick.homeWin ? game.homeTeam.name : game.awayTeam.name;
                const isWinner = winningTeam === pickedWinner;
                const showPicks = game.disabled || isAdmin;

                if (!showPicks) {
                  return (
                    <div style={baseRowStyle} key={user.name}>
                      <span style={mobileStyles.pickName}>{getDisplayName(userDisplayNames.get(user.name), user.name)}{isMe && <YouChip />}</span>
                      <span style={mobileStyles.lockedText}>Locked</span>
                    </div>
                  );
                }


                const pickedTeam = pick.awayWin ? game.awayTeam : game.homeTeam;
                const pickedShortName = pickedTeam.shortName || pickedTeam.name;

                const pickKey = `${gameId}-${user.name}`;
                const isExpanded = expandedPick === pickKey;

                return (
                  <div key={user.name}>
                    <div
                      style={{
                        ...baseRowStyle,
                        ...(game.isPickScore && pick.scoreDifference !== undefined ? { cursor: 'pointer' } : {}),
                      }}
                      onClick={() => {
                        if (game.isPickScore && pick.scoreDifference !== undefined) {
                          setExpandedPick(isExpanded ? null : pickKey);
                        }
                      }}
                    >
                      <span style={mobileStyles.pickName}>{getDisplayName(userDisplayNames.get(user.name), user.name)}{isMe && <YouChip />}</span>
                      <span style={mobileStyles.pickValue}>
                        {game.isPickScore ? (
                          // Pill marks the pick-score winner(s)
                          pick.isWinningPick ? (
                            <WinPill>
                              {pick.awayScore} – {pick.homeScore}
                              {pick.isExactMatch && ' ★'}
                            </WinPill>
                          ) : (
                            <>{pick.awayScore} – {pick.homeScore}</>
                          )
                        ) : (
                          <>
                            {pickedShortName}
                            {winningTeam && <ResultDot correct={isWinner} />}
                          </>
                        )}
                      </span>
                    </div>
                    {isExpanded && (
                      <div style={{
                        padding: '0 14px 10px',
                        fontSize: '12px',
                        color: pick.isExactMatch ? soft.ok : soft.muted,
                        fontWeight: pick.isExactMatch ? 500 : 400,
                        backgroundColor: isMe ? soft.meBg : '#FFFFFF',
                        boxShadow: isMe ? meRowFirstCellShadow : undefined,
                        textAlign: 'right',
                      }}>
                        {pick.isExactMatch ? 'Exact Score!' : `${pick.scoreDifference} points away`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );

    // A finished game's winner, from its final score (works for every past
    // week without needing scoring to have run).
    const winnerOf = (game, gameId) => (game.homeScore !== undefined && game.awayScore !== undefined)
      ? (Number(game.homeScore) > Number(game.awayScore) ? game.homeTeam.name : game.awayTeam.name)
      : (winningTeams && winningTeams[gameId]);

    const renderDesktopPickCell = (game, gameId, user) => {
      const pick = picksByUser[user.name]?.[game.id];
      if (!pick) return null;

      const winningTeam = winnerOf(game, gameId);
      const pickedWinner = pick.homeWin ? game.homeTeam.name : game.awayTeam.name;
      const isWinner = winningTeam === pickedWinner;
      const showPicks = game.disabled || isAdmin;

      if (!showPicks) return <span style={{ color: soft.faint }}>Locked</span>;

      if (game.isPickScore) {
        const text = `${pick.awayScore} – ${pick.homeScore}`;
        const score = (
          <div style={styles.score}>
            {pick.isWinningPick ? <WinPill>{text}</WinPill> : text}
          </div>
        );
        return pick.scoreDifference !== undefined ? (
          <Tooltip title={pick.isExactMatch ? "Exact Score!" : `${pick.scoreDifference} points away`} enterDelay={500}>
            {score}
          </Tooltip>
        ) : score;
      }

      if (winningTeam) {
        // Game finished — show ✓ or ✗ instead of checkboxes
        return <ResultDot correct={isWinner} />;
      }

      // Game not finished — show checkboxes
      return (
        <div style={styles.score}>
          <input className="check" type='checkbox' checked={pick.awayWin} disabled={true} />
          <input className="check" type='checkbox' checked={pick.homeWin} disabled={true} />
        </div>
      );
    };

    // Desktop: one row per player, one column per game, scrolling sideways
    // with the player and Week pts columns pinned.
    const renderDesktopPicks = () => (
      <TableContainer sx={{ ...softContainerSx, overflowX: 'auto', maxWidth: '100%', ...fadeWhileLoading(isLoading) }}>
        <Table size="small" aria-label="week picks" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...pickHeadCellSx, ...stickyNameSx }}>Player</TableCell>
              {games && games.map((game) => {
                const gameId = `${game.awayTeam?.name || 'away'}-${game.homeTeam?.name || 'home'}`;
                const hasFinal = game.disabled && game.homeScore !== undefined && game.awayScore !== undefined;
                return (
                  <TableCell key={gameId} sx={{ ...pickHeadCellSx, minWidth: '96px' }}>
                    <div style={styles.gameHeader}>
                      {game.awayTeam.logo && (
                        <img style={styles.icon} src={getLogoUrl(game.awayTeam.logo)} alt={game.awayTeam.name} />
                      )}
                      <span style={styles.vs}>vs</span>
                      {game.homeTeam.logo && (
                        <img style={styles.icon} src={getLogoUrl(game.homeTeam.logo)} alt={game.homeTeam.name} />
                      )}
                    </div>
                    {/* Final score under the logos once the game is complete */}
                    <div style={styles.finalScore}>{hasFinal ? `${game.awayScore} – ${game.homeScore}` : '\u00A0'}</div>
                  </TableCell>
                );
              })}
              <TableCell sx={{ ...pickHeadCellSx, ...stickyWeekSx }}>Week pts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allPicksForWeek && allPicksForWeek.map((user) => {
              const isMe = isCurrentUser(user.name, currentUser);
              const rowBackground = isMe ? soft.meBg : '#FFFFFF';
              const displayName = getDisplayName(userDisplayNames.get(user.name), user.name);
              const points = weeklyPoints[user.name];
              return (
                <TableRow key={user.name} data-me={isMe ? 'true' : undefined} sx={{ backgroundColor: rowBackground }}>
                  <TableCell
                    sx={{
                      ...pickCellSx,
                      ...stickyNameSx,
                      backgroundColor: rowBackground,
                      fontWeight: isMe ? 500 : 400,
                      ...(isMe && { boxShadow: `${meRowFirstCellShadow}, ${stickyNameSx.boxShadow}` }),
                    }}
                    title={displayName}
                  >
                    {displayName}{isMe && <YouChip />}
                  </TableCell>
                  {games && games.map((game) => {
                    const gameId = `${game.awayTeam?.name || 'away'}-${game.homeTeam?.name || 'home'}`;
                    return (
                      <TableCell key={gameId} sx={pickCellSx}>
                        {renderDesktopPickCell(game, gameId, user)}
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ ...pickCellSx, ...stickyWeekSx, backgroundColor: rowBackground, fontWeight: 500 }}>
                    {points ? formatPoints(points.total) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );

    return (
        <Box style={{
            ...styles.container,
            ...(isMobile && { marginLeft: '8px', marginRight: '8px', marginTop: '80px' }),
        }}>
            <section style={{
                ...styles.leaderboard,
                ...(isMobile && { maxWidth: '100%', padding: '10px 0' }),
            }}>
              <h3>{season} Standings</h3>
              {
                isLoading && <Box sx={{ width: '100%' }}>
                  <LinearProgress />
                </Box>
              }
              {fetchError && (
                <Box sx={{ color: 'error.main', textAlign: 'center', mt: 2 }}>
                  {fetchError}
                </Box>
              )}
              <LeaderboardTable season={season} currentWeek={numberOfWeeks} onSelectWeek={showWeekPicks}/>
            </section>

            <section ref={picksSectionRef} style={{
                ...styles.picks,
                ...(isMobile && { padding: '10px 0', width: '100%' }),
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                <span>Week:</span>
                {isMobile ? (
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select value={page} onChange={(e) => turnPage(e, e.target.value)}>
                      {Array.from({ length: numberOfWeeks }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Pagination count={numberOfWeeks} shape="rounded" page={page} onChange={turnPage}/>
                )}
              </div>
              {myPicks && (
                <WeekSummary
                  week={page}
                  games={games}
                  picks={picksByUser[myPicks.name]}
                  myPoints={weeklyPoints[myPicks.name]}
                  weeklyPoints={weeklyPoints}
                  isMobile={isMobile}
                />
              )}
              {isMobile ? renderMobilePicks() : renderDesktopPicks()}
            </section>
        </Box>
    );
}

export default Leaderboard;
